import { format, formatDistanceToNow } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import i18n from '../i18n';

export function getIntlLocale(language = i18n.resolvedLanguage || i18n.language) {
  if (!language) return 'en-US';
  return language.startsWith('tr') ? 'tr-TR' : 'en-US';
}

export function getDateFnsLocale(language = i18n.resolvedLanguage || i18n.language) {
  return language?.startsWith('tr') ? tr : enUS;
}

export function formatDate(value, options = {}, language) {
  if (!value) return '';
  return new Intl.DateTimeFormat(getIntlLocale(language), options).format(new Date(value));
}

export function formatDateTime(value, options = {}, language) {
  return formatDate(
    value,
    {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...options,
    },
    language,
  );
}

export function formatNumber(value, options = {}, language) {
  return new Intl.NumberFormat(getIntlLocale(language), options).format(Number(value || 0));
}

export function formatCurrency(value, currency = 'USD', options = {}, language) {
  return new Intl.NumberFormat(getIntlLocale(language), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    ...options,
  }).format(Number(value || 0));
}

export function formatRelativeDistance(value, options = {}, language) {
  if (!value) return '';
  return formatDistanceToNow(new Date(value), {
    addSuffix: true,
    locale: getDateFnsLocale(language),
    ...options,
  });
}

export function formatWithPattern(value, pattern, language) {
  if (!value) return '';
  return format(new Date(value), pattern, { locale: getDateFnsLocale(language) });
}

const ERROR_CODE_MAP = {
  UNKNOWN_ERROR: 'errors.unknown',
  AUTH_REQUIRED: 'errors.authRequired',
  ACCESS_DENIED: 'errors.accessDenied',
  VALIDATION_ERROR: 'errors.validation',
  LIMIT_REACHED: 'errors.limitReached',
  FEATURE_LOCKED: 'errors.featureLocked',
};

const ERROR_MESSAGE_MAP = {
  'Registration failed. Please try again.': 'errors.registrationFailed',
  'Failed to load inbox.': 'errors.loadInbox',
  'Failed to load campaigns': 'errors.loadCampaigns',
  'Failed to save campaign': 'errors.saveCampaign',
  'Failed to delete campaign': 'errors.deleteCampaign',
  'Bulk action failed': 'errors.bulkAction',
  'Reply failed': 'errors.replyFailed',
  'Archive failed': 'errors.archiveFailed',
  'Update failed': 'errors.updateFailed',
};

export function normalizeApiError(error, t, fallbackKey = 'errors.generic') {
  if (!error) return t(fallbackKey);

  if (error.isAppError && error.code && ERROR_CODE_MAP[error.code]) {
    return t(ERROR_CODE_MAP[error.code]);
  }

  const message =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message;

  if (message && ERROR_MESSAGE_MAP[message]) {
    return t(ERROR_MESSAGE_MAP[message]);
  }

  return message || t(fallbackKey);
}
